import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  type PropsWithChildren,
} from 'react';
import { rpcClient } from 'src/api/rpc/client';
import type { ModuleKey, PageKey, MessageAttachment } from '@erp/rpc';
import type { FrontendToolDefinition } from '@/hooks/useFrontendTool';

export interface MessageToolResult {
  toolCallId: string;
  toolName: string;
  args: Record<string, unknown>;
  result: unknown;
}

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  toolResults?: MessageToolResult[];
  attachments?: MessageAttachment[];
  /**
   * Stable DB id (`ai_thread_messages.id`) for assistant turns once the
   * backend has persisted them — used as `subjectId` for per-message user
   * feedback. Absent on user turns and on assistant turns that failed to
   * persist (e.g., DB write error after streaming).
   */
  dbMessageId?: string;
  /** Langfuse traceId for the turn that produced this assistant message. */
  traceId?: string | null;
  /**
   * Langfuse observation (span) id for the AI SDK's `ai.streamText` span
   * within the trace. When present, user-feedback scores attach to this
   * specific observation; otherwise they fall back to trace-level.
   */
  observationId?: string | null;
  hasScreenshot?: boolean;
  /** Base64-encoded screenshot data URL for display in the chat bubble. */
  screenshotDataUrl?: string;
}

export type ImageAttachmentValue = {
  imageData: string;
  mimeType: 'image/jpeg' | 'image/png';
};

export interface ToolCall {
  toolCallId: string;
  toolName: string;
  userFacingName?: string;
  args: Record<string, unknown>;
  result?: unknown;
  status: 'pending' | 'complete' | 'error';
}

/**
 * Generic "agent yields to the user" primitive. When a tool call produces a
 * result that requires a user decision (accept / discard / …) before the
 * module state should change, the owning module parks an `AgentYield` on
 * agent state and the chat card surfaces it. The context itself knows
 * nothing about the specific payload shape — `payload` is `unknown` and
 * consumers cast at their own boundary. Today's concrete use case is the
 * workflow-proposal Apply Changes card; future yields might be clarifying
 * questions, approval requests, or anything else that pauses the agent for
 * user input.
 */
export interface AgentYield {
  /** Stable id for this specific yield (usually derived from toolCallId). */
  yieldId: string;
  /** The tool call whose result produced this yield. */
  toolCallId: string;
  /** Name of the tool that produced the yield (for consumer dispatch). */
  toolName: string;
  /** Optional human-readable label for the chat card. */
  summary?: string;
  /** Module-specific payload. Consumers cast to their type. */
  payload: unknown;
  createdAt: Date;
}

/** Decision string recorded when the user resolves a yield. */
export type YieldDecision = 'accepted' | 'discarded';

type ToolCallListener = (
  toolCallId: string,
  toolName: string,
  args: Record<string, unknown>,
) => void;

type ToolResultListener = (
  toolCallId: string,
  toolName: string,
  result: unknown,
) => void;

type DraftSnapshotListener = (toolCallId: string, draft: unknown) => void;

type ToolInputStreamListener = (
  toolCallId: string,
  toolName: string,
  partialInput: string,
) => void;

export interface AgentStateContextData {
  messages: Message[];
  streamingText: string;
  isLoading: boolean;
  isCapturingScreen: boolean;
  error: string | null;
  activeToolCalls: ToolCall[];
  currentThreadId: string | null;
  currentModuleKey: ModuleKey | null;
  /**
   * Yields currently awaiting a user decision. Today the UX rule is
   * "second proposal replaces the first", so this list holds at most one
   * entry — modelled as a list so we can lift that restriction later
   * without widening types across consumers.
   */
  activeYields: readonly AgentYield[];
  /**
   * Replace the set of active yields. Modules call this from a
   * tool-result listener when a result represents a pending decision.
   * Today callers pass a single-element array (or empty to clear);
   * future work can pass multiple.
   */
  setActiveYields: (next: readonly AgentYield[]) => void;
  /**
   * Terminal decision for every yield the user has acted on, keyed by
   * toolCallId. Lives in shared state so the chat card (rendered from
   * ChatAgentRenderer) and the producing module (e.g., WorkflowEditor)
   * see the same value within a single React commit, regardless of child
   * render order.
   */
  resolvedYields: ReadonlyMap<string, YieldDecision>;
  sendMessage: (
    content: string,
    moduleKey: ModuleKey,
    currentPage: string,
    moduleContext?: Record<string, unknown>,
    pageKey?: PageKey,
    frontendTools?: FrontendToolDefinition[],
    imageAttachment?: ImageAttachmentValue | Promise<ImageAttachmentValue>,
    attachments?: MessageAttachment[],
  ) => Promise<void>;
  reset: () => void;
  cancel: () => void;
  /**
   * Record the outcome of a specific yield for telemetry and remove it
   * from the active list. Callers apply their module-specific side
   * effects (commit workflow, etc.) themselves before/after calling
   * this — the context only owns yield state and fires the telemetry
   * RPC.
   */
  resolveYield: (toolCallId: string, decision: YieldDecision) => void;
  /**
   * Clear the terminal decision for a specific yield, e.g. when the user
   * reverts an applied yield and the chat card should return to an
   * actionable state.
   */
  clearYieldResolution: (toolCallId: string) => void;
  /**
   * Subscribe to completed tool-result events as they stream in. Modules
   * use this to react to tool-results (e.g., to populate an `AgentYield`)
   * without the context itself needing to know tool names. Returns an
   * unregister function.
   */
  registerToolResultListener: (listener: ToolResultListener) => () => void;
  registerToolCallListener: (listener: ToolCallListener) => () => void;
  registerDraftSnapshotListener: (
    listener: DraftSnapshotListener,
  ) => () => void;
  /**
   * Subscribe to streaming partial tool input as the model writes a tool
   * call's JSON arguments, before the call completes. Modules use this to
   * progressively render an incomplete spec (e.g. an email-template
   * proposal streaming into the editor/preview). Returns an unregister
   * function.
   */
  registerToolInputStreamListener: (
    listener: ToolInputStreamListener,
  ) => () => void;
  /**
   * AR Vision Demo (GEN-2247) — demo-only escape hatch. Streams a canned
   * assistant turn (the user message + narration text streamed
   * word-by-word) into the existing chat UI WITHOUT hitting the backend, then
   * runs an optional callback once narration finishes (used to fire the
   * scripted page tools). Delete alongside frontend/src/demo/ar. Returns a
   * promise that resolves after narration + callback complete.
   */
  runDemoScriptedTurn: (
    userMessage: string,
    narration: string,
    onComplete?: () => void | Promise<void>,
    /**
     * Runs AFTER the user message renders but BEFORE the narration streams —
     * used to fire a beat's tools first (e.g. build the workflow) so Nami's
     * reply only appears once that finishes. The loading indicator shows
     * meanwhile.
     */
    beforeNarration?: () => void | Promise<void>,
  ) => Promise<void>;
}

const AgentStateContext = createContext<AgentStateContextData | undefined>(
  undefined,
);

let messageIdCounter = 0;

function generateMessageId(): string {
  return `msg-${Date.now()}-${++messageIdCounter}`;
}

// AR Vision Demo (GEN-2247) — brief pause (ms) where Nami "thinks" before
// streaming each scripted reply. Delete alongside frontend/src/demo/ar.
const DEMO_THINKING_PAUSE_MS = 700;

export function AgentStateProvider({ children }: PropsWithChildren) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [streamingText, setStreamingText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCapturingScreen, setIsCapturingScreen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeToolCalls, setActiveToolCalls] = useState<ToolCall[]>([]);
  const [currentThreadId, setCurrentThreadId] = useState<string | null>(null);
  const [currentModuleKey, setCurrentModuleKey] = useState<ModuleKey | null>(
    null,
  );
  const [activeYields, setActiveYieldsState] = useState<readonly AgentYield[]>(
    [],
  );
  const [resolvedYields, setResolvedYields] = useState<
    Map<string, YieldDecision>
  >(() => new Map());

  const setActiveYields = useCallback((next: readonly AgentYield[]) => {
    setActiveYieldsState(next);
  }, []);

  const toolCallListenersRef = useRef<Set<ToolCallListener>>(new Set());
  const registerToolCallListener = useCallback((listener: ToolCallListener) => {
    toolCallListenersRef.current.add(listener);
    return () => {
      toolCallListenersRef.current.delete(listener);
    };
  }, []);

  const toolResultListenersRef = useRef<Set<ToolResultListener>>(new Set());
  const registerToolResultListener = useCallback(
    (listener: ToolResultListener) => {
      toolResultListenersRef.current.add(listener);
      return () => {
        toolResultListenersRef.current.delete(listener);
      };
    },
    [],
  );

  const draftSnapshotListenersRef = useRef<Set<DraftSnapshotListener>>(
    new Set(),
  );
  const registerDraftSnapshotListener = useCallback(
    (listener: DraftSnapshotListener) => {
      draftSnapshotListenersRef.current.add(listener);
      return () => {
        draftSnapshotListenersRef.current.delete(listener);
      };
    },
    [],
  );

  const toolInputStreamListenersRef = useRef<Set<ToolInputStreamListener>>(
    new Set(),
  );
  const registerToolInputStreamListener = useCallback(
    (listener: ToolInputStreamListener) => {
      toolInputStreamListenersRef.current.add(listener);
      return () => {
        toolInputStreamListenersRef.current.delete(listener);
      };
    },
    [],
  );

  const abortControllerRef = useRef<AbortController | null>(null);
  const streamSessionIdRef = useRef(0);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const threadIdRef = useRef(currentThreadId);
  threadIdRef.current = currentThreadId;
  const moduleKeyRef = useRef(currentModuleKey);
  moduleKeyRef.current = currentModuleKey;
  // Tracks the pageKey of the most recently sent message so resolveYield can
  // stamp it on the yield-decision telemetry without callers re-passing it.
  const pageKeyRef = useRef<PageKey | undefined>(undefined);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const cancel = useCallback(() => {
    abortControllerRef.current?.abort();
    setIsLoading(false);
  }, []);

  const sendMessage = useCallback(
    async (
      content: string,
      moduleKey: ModuleKey,
      currentPage: string,
      moduleContext?: Record<string, unknown>,
      pageKey?: PageKey,
      frontendTools?: FrontendToolDefinition[],
      imageAttachment?: ImageAttachmentValue | Promise<ImageAttachmentValue>,
      attachments?: MessageAttachment[],
    ) => {
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();

      const myStreamSession = ++streamSessionIdRef.current;

      const prevModuleKey = moduleKeyRef.current;
      const moduleChanged =
        prevModuleKey !== null && prevModuleKey !== moduleKey;
      if (moduleChanged) {
        setMessages([]);
        setCurrentThreadId(null);
        setActiveYieldsState([]);
        setResolvedYields(new Map());
      }
      setCurrentModuleKey(moduleKey);
      pageKeyRef.current = pageKey;

      const userMessage: Message = {
        id: generateMessageId(),
        role: 'user',
        content,
        timestamp: new Date(),
        hasScreenshot: !!imageAttachment,
        attachments: attachments?.length ? attachments : undefined,
      };

      const existingMessages = moduleChanged ? [] : messagesRef.current;
      setMessages((prev) =>
        moduleChanged ? [userMessage] : [...prev, userMessage],
      );
      setStreamingText('');
      setIsLoading(true);
      setError(null);
      setActiveToolCalls([]);

      let resolvedImage: ImageAttachmentValue | undefined;
      if (imageAttachment) {
        if (imageAttachment instanceof Promise) {
          setIsCapturingScreen(true);
          try {
            resolvedImage = await imageAttachment;
          } catch (err) {
            console.warn(
              '[agent-state] Screen capture failed, sending without image:',
              err,
            );
          } finally {
            setIsCapturingScreen(false);
          }
        } else {
          resolvedImage = imageAttachment;
        }
        if (resolvedImage) {
          const dataUrl = `data:${resolvedImage.mimeType};base64,${resolvedImage.imageData}`;
          setMessages((prev) =>
            prev.map((m) =>
              m.id === userMessage.id
                ? { ...m, screenshotDataUrl: dataUrl }
                : m,
            ),
          );
        }
      }

      try {
        const allMessages = [...existingMessages, userMessage];
        const apiMessages = allMessages.map((m, i) => {
          if (i === allMessages.length - 1 && resolvedImage) {
            return {
              role: 'user' as const,
              content: [
                { type: 'text' as const, text: m.content },
                {
                  type: 'image' as const,
                  imageData: resolvedImage.imageData,
                  mimeType: resolvedImage.mimeType,
                },
              ],
              ...(m.attachments?.length && { attachments: m.attachments }),
            };
          }
          return {
            role: m.role as 'user' | 'assistant',
            content: m.content,
            ...(m.attachments?.length && { attachments: m.attachments }),
          };
        });

        const threadId = moduleChanged
          ? undefined
          : (threadIdRef.current ?? undefined);

        const frontendToolMap = new Map(
          (frontendTools ?? []).map((t) => [t.name, t]),
        );

        const iterator = await rpcClient.ai.chatAgent.chat(
          {
            moduleKey,
            pageKey,
            currentPage,
            threadId,
            messages: apiMessages,
            moduleContext,
            frontendTools: frontendTools?.map((t) => t.schema),
          },
          { signal: abortControllerRef.current.signal },
        );

        let fullText = '';
        const completedToolResults: MessageToolResult[] = [];
        const localToolCalls: Map<
          string,
          { toolName: string; args: Record<string, unknown> }
        > = new Map();

        for await (const event of iterator) {
          if (myStreamSession !== streamSessionIdRef.current) {
            break;
          }

          switch (event.type) {
            case 'text-delta':
              fullText += event.text;
              setStreamingText(fullText);
              break;

            case 'tool-call':
              localToolCalls.set(event.toolCallId, {
                toolName: event.toolName,
                args: event.args,
              });
              setActiveToolCalls((prev) => [
                ...prev,
                {
                  toolCallId: event.toolCallId,
                  toolName: event.toolName,
                  userFacingName: event.userFacingName,
                  args: event.args,
                  status: 'pending',
                },
              ]);
              toolCallListenersRef.current.forEach((listener) => {
                try {
                  listener(event.toolCallId, event.toolName, event.args);
                } catch (err) {
                  console.error('tool-call listener threw', err);
                }
              });

              // Awaitable frontend tools run their handler now (the backend is
              // blocking the agent until we POST a result) and round-trip the
              // return value back so the agent sees it in the same turn. Fire
              // async so the stream loop keeps receiving events.
              {
                const awaitableFt = frontendToolMap.get(event.toolName);
                if (awaitableFt?.awaitable) {
                  const { toolCallId } = event;
                  void (async () => {
                    try {
                      const result = await awaitableFt.execute(
                        event.args as never,
                      );
                      await rpcClient.ai.chatAgent.submitFrontendToolResult({
                        toolCallId,
                        result,
                      });
                    } catch (err) {
                      console.error(
                        `awaitable frontend tool ${awaitableFt.name} threw`,
                        err,
                      );
                      try {
                        await rpcClient.ai.chatAgent.submitFrontendToolResult({
                          toolCallId,
                          result: { ok: false, error: String(err) },
                        });
                      } catch (postErr) {
                        console.error(
                          'failed to submit frontend tool error result',
                          postErr,
                        );
                      }
                    }
                  })();
                }
              }
              break;

            case 'tool-result': {
              const tc = localToolCalls.get(event.toolCallId);
              completedToolResults.push({
                toolCallId: event.toolCallId,
                toolName: event.toolName,
                args: tc?.args ?? {},
                result: event.result,
              });
              setActiveToolCalls((prev) =>
                prev.map((existing) =>
                  existing.toolCallId === event.toolCallId
                    ? {
                        ...existing,
                        result: event.result,
                        status: 'complete' as const,
                      }
                    : existing,
                ),
              );
              // Execute fire-and-forget frontend tool handlers here. Awaitable
              // tools already ran on the tool-call event (and round-tripped
              // their result), so skip them to avoid double execution.
              const ftDef = frontendToolMap.get(event.toolName);
              if (ftDef && !ftDef.awaitable) {
                try {
                  void ftDef.execute(event.result as never);
                } catch (err) {
                  console.error(`frontend tool ${event.toolName} threw`, err);
                }
              }

              // Dispatch to module-registered listeners so module-specific
              // reactions (e.g. populating an AgentYield for the workflows
              // editor) stay out of this generic context.
              toolResultListenersRef.current.forEach((listener) => {
                try {
                  listener(event.toolCallId, event.toolName, event.result);
                } catch (err) {
                  console.error('tool-result listener threw', err);
                }
              });
              break;
            }

            case 'draft-snapshot':
              draftSnapshotListenersRef.current.forEach((listener) => {
                try {
                  listener(event.toolCallId, event.draft);
                } catch (err) {
                  console.error('draft-snapshot listener threw', err);
                }
              });
              break;

            case 'tool-input-delta':
              toolInputStreamListenersRef.current.forEach((listener) => {
                try {
                  listener(
                    event.toolCallId,
                    event.toolName,
                    event.partialInput,
                  );
                } catch (err) {
                  console.error('tool-input-delta listener threw', err);
                }
              });
              break;

            case 'thread-info':
              setCurrentThreadId(event.threadId);
              break;

            case 'assistant-message-saved':
              // Backend persisted the assistant turn after `finish`. Patch
              // the most recent assistant message with its DB id, traceId,
              // and observationId so user-feedback widgets have a durable
              // subject + Langfuse observation to attach to.
              setMessages((prev) => {
                for (let i = prev.length - 1; i >= 0; i--) {
                  if (prev[i]?.role === 'assistant') {
                    const next = prev.slice();
                    const target = next[i];
                    if (!target) return prev;
                    next[i] = {
                      ...target,
                      dbMessageId: event.messageId,
                      traceId: event.traceId,
                      observationId: event.observationId,
                    };
                    return next;
                  }
                }
                return prev;
              });
              break;

            case 'finish':
              if (fullText) {
                const assistantMessage: Message = {
                  id: generateMessageId(),
                  role: 'assistant',
                  content: fullText,
                  timestamp: new Date(),
                  toolResults:
                    completedToolResults.length > 0
                      ? completedToolResults
                      : undefined,
                };
                setMessages((prev) => [...prev, assistantMessage]);
              }
              setStreamingText('');
              break;

            case 'error':
              setError(event.message);
              break;
          }
        }
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          setError(err instanceof Error ? err.message : 'Stream error');
        }
      } finally {
        setIsLoading(false);
        setIsCapturingScreen(false);
        setActiveToolCalls((prev) =>
          prev.some((tc) => tc.status === 'pending')
            ? prev.map((tc) =>
                tc.status === 'pending'
                  ? { ...tc, status: 'complete' as const }
                  : tc,
              )
            : prev,
        );
      }
    },
    [],
  );

  // AR Vision Demo (GEN-2247) — demo-only scripted turn. See interface doc.
  // Delete alongside frontend/src/demo/ar.
  const runDemoScriptedTurn = useCallback(
    async (
      userMessage: string,
      narration: string,
      onComplete?: () => void | Promise<void>,
      beforeNarration?: () => void | Promise<void>,
    ) => {
      abortControllerRef.current?.abort();
      const userMsg: Message = {
        id: generateMessageId(),
        role: 'user',
        content: userMessage,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMsg]);
      setStreamingText('');
      setIsLoading(true);
      setError(null);
      setActiveToolCalls([]);

      // Optionally run the beat's tools first (e.g. build the workflow) while
      // the loading indicator shows, so the narration lands once it's done.
      if (beforeNarration) {
        try {
          await beforeNarration();
        } catch (err) {
          console.error('[ar-demo] scripted turn beforeNarration threw', err);
        }
      }

      // Brief "thinking" beat before the response streams. Without it the
      // scripted turn snaps to text instantly, which reads as canned; the
      // loading indicator shows during this pause (and the chat scrolls to it).
      await new Promise((r) => setTimeout(r, DEMO_THINKING_PAUSE_MS));

      // Stream the narration word-by-word for a live-typing feel.
      const words = narration.split(' ');
      let acc = '';
      for (let i = 0; i < words.length; i++) {
        acc += (i === 0 ? '' : ' ') + words[i];
        setStreamingText(acc);
        await new Promise((r) => setTimeout(r, 24));
      }

      const assistantMessage: Message = {
        id: generateMessageId(),
        role: 'assistant',
        content: narration,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
      setStreamingText('');
      setIsLoading(false);

      if (onComplete) {
        try {
          await onComplete();
        } catch (err) {
          console.error('[ar-demo] scripted turn onComplete threw', err);
        }
      }
    },
    [],
  );

  const reset = useCallback(() => {
    abortControllerRef.current?.abort();
    setMessages([]);
    setStreamingText('');
    setIsLoading(false);
    setIsCapturingScreen(false);
    setError(null);
    setActiveToolCalls([]);
    setCurrentThreadId(null);
    setCurrentModuleKey(null);
    pageKeyRef.current = undefined;
    setActiveYieldsState([]);
    setResolvedYields(new Map());
    streamSessionIdRef.current++;
  }, []);

  const activeYieldsRef = useRef(activeYields);
  activeYieldsRef.current = activeYields;

  const resolveYield = useCallback(
    (toolCallId: string, decision: YieldDecision) => {
      const current = activeYieldsRef.current.find(
        (y) => y.toolCallId === toolCallId,
      );
      if (!current) return;
      setResolvedYields((prev) => {
        const next = new Map(prev);
        next.set(current.toolCallId, decision);
        return next;
      });
      setActiveYieldsState((prev) =>
        prev.filter((y) => y.toolCallId !== toolCallId),
      );
      const threadId = threadIdRef.current;
      // Fire-and-forget telemetry — surface errors to console so backend
      // outages don't block the UX.
      rpcClient.ai.chatAgent
        .recordYieldDecision({
          yieldId: current.yieldId,
          toolCallId: current.toolCallId,
          toolName: current.toolName,
          decision,
          threadId: threadId ?? undefined,
          pageKey: pageKeyRef.current,
        })
        .catch((err: unknown) => {
          console.warn('recordYieldDecision failed', err);
        });
    },
    [],
  );

  const clearYieldResolution = useCallback((toolCallId: string) => {
    setResolvedYields((prev) => {
      if (!prev.has(toolCallId)) return prev;
      const next = new Map(prev);
      next.delete(toolCallId);
      return next;
    });
  }, []);

  return (
    <AgentStateContext.Provider
      value={{
        messages,
        streamingText,
        isLoading,
        isCapturingScreen,
        error,
        activeToolCalls,
        currentThreadId,
        currentModuleKey,
        activeYields,
        setActiveYields,
        resolvedYields,
        sendMessage,
        reset,
        cancel,
        resolveYield,
        clearYieldResolution,
        registerToolResultListener,
        registerToolCallListener,
        registerDraftSnapshotListener,
        registerToolInputStreamListener,
        runDemoScriptedTurn,
      }}
    >
      {children}
    </AgentStateContext.Provider>
  );
}

export function useAgentState() {
  const context = useContext(AgentStateContext);
  if (context === undefined) {
    throw new Error('useAgentState must be used within an AgentStateProvider');
  }
  return context;
}
